import React from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, BrainCircuit, Shield, Video, 
  Bell, Swords, CheckCircle2
} from 'lucide-react';

const pillars = [
  {
    icon: Video,
    title: "Meeting Intelligence",
    tagline: "Every meeting captured. Every insight extracted. Automatically.",
    color: "from-hubspot to-orange-400",
    features: [
      "Auto-ingest meetings from Google Calendar and Zoom",
      "AI transcription with action item extraction",
      "Post-meeting CRM updates written automatically",
      "Meeting prep briefs with deal context and talking points",
      "Daily and weekly meeting digests for managers",
    ],
  },
  {
    icon: BarChart3,
    title: "Defensible Forecasting",
    tagline: "Board-ready numbers backed by evidence, not gut feel.",
    color: "from-primary to-primary/70",
    features: [
      "CRM stays accurate: data updates autonomously from every interaction",
      "AI-scored deal confidence from real buyer signals",
      "Commit / Best Case / Pipeline buckets backed by evidence",
      "Historical backtesting and calibration to improve accuracy",
      "Forecast Advisor: query your pipeline in natural language",
    ],
  },
  {
    icon: Shield,
    title: "Deep Deal Intelligence",
    tagline: "Know which deals are real before they burn your quarter.",
    color: "from-destructive to-red-400",
    features: [
      "Detects single-threaded deals with no executive sponsor",
      "MEDDIC qualification scoring, auto-populated from signals",
      "Buyer engagement scoring reveals who's actually engaged",
      "Going-dark alerts when buyer response time spikes",
      "Surfaces contradictions between rep claims and CRM data",
    ],
  },
  {
    icon: BrainCircuit,
    title: "Coaching & Rep Development",
    tagline: "Identify needs, track progression, build a team that compounds.",
    color: "from-purple-500 to-purple-400",
    features: [
      "AI coaching insights based on call recordings and win/loss patterns",
      "Rep performance benchmarking with evidence-based metrics",
      "Skill gap identification: discovery, multi-threading, follow-up cadence",
      "Personalised action plans assigned and tracked to completion",
      "Business continuity: full deal context transfers when reps leave",
    ],
  },
  {
    icon: Bell,
    title: "Proactive Alerts & Nudges",
    tagline: "Stop firefighting. Get ahead of problems.",
    color: "from-green-500 to-green-400",
    features: [
      "Risk alerts pushed to Slack when deals stall or engagement drops",
      "Automated follow-up chasing for ghosted prospects",
      "Weekly CRO digest with pipeline health and top actions",
      "Action items tracked from meetings to resolution",
      "Smart notifications: only what matters, when it matters",
    ],
  },
  {
    icon: Swords,
    title: "Competitive Intelligence",
    tagline: "Know what you're up against. Win more head-to-heads.",
    color: "from-amber-500 to-amber-400",
    features: [
      "Auto-detect competitor mentions from calls and emails",
      "AI-generated battlecards with win/loss data per competitor",
      "Track win rate against specific competitors over time",
      "Surface common objections and winning counter-arguments",
      "Feed competitive intel into real-time meeting coaching",
    ],
  },
];

export const FeaturePillars = () => {
  return (
    <section className="w-full py-20 md:py-32 bg-background">
      <div className="container px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Six capabilities. <span className="text-gradient-teal">One AI agent.</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From meeting capture to board-ready forecasts — NoQ5 handles the revenue ops your team shouldn't be doing manually.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.06 }}
              viewport={{ once: true }}
              className="group bg-card rounded-2xl border border-border p-6 hover:border-primary/30 hover:shadow-card transition-all duration-300"
            >
              {/* Header */}
              <div className="flex items-start gap-3 mb-5">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                  <pillar.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">{pillar.title}</h3>
                  <p className="text-sm text-primary italic">{pillar.tagline}</p>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-2.5">
                {pillar.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
